import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle, ArrowDown, FileText } from 'lucide-react';

const NotesRow = ({ client, onSave, onBulkApply }) => {
    const [note, setNote] = useState(client.notes || '');

    useEffect(() => {
        setNote(client.notes || '');
    }, [client]);

    const handleBlur = () => {
        if ((client.notes || '') !== note) {
            onSave(client.id, { notes: note }, false, ['notes']);
        }
    };

    return (
        <tr className="hover:bg-blue-50/60 group transition-colors odd:bg-white even:bg-blue-50/20">
            <td className="px-4 py-2 text-xs font-bold text-slate-800 border-r border-blue-100">
                {client.name}
            </td>
            <td className="px-2 py-1 border-r border-blue-100 relative focus-within:bg-blue-50">
                <div className="flex items-center">
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        onBlur={handleBlur}
                        className="w-full bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 rounded text-slate-700 placeholder-slate-400 resize-none focus:bg-white"
                        rows={2}
                        placeholder="Add notes..."
                    />
                    <button
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onBulkApply(note, client.id);
                        }}
                        className="hidden group-hover:flex items-center justify-center p-1 text-slate-300 hover:text-blue-600 hover:bg-blue-100 rounded mr-1"
                        title={`Apply "${note}" to all selected clients`}
                    >
                        <ArrowDown className="w-3 h-3" />
                    </button>
                </div>
            </td>
        </tr>
    );
};

const NotesListModal = ({ selectedClients, onClose, onUpdateClient }) => {
    const clientsToDisplay = Array.from(selectedClients);

    const handleBulkApply = (value, sourceClientId) => {
        if (!clientsToDisplay.length) return;
        if (confirm(`Apply notes to all ${clientsToDisplay.length} selected clients?`)) {
            onUpdateClient(sourceClientId, { notes: value }, true, ['notes']);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-indigo-600">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-white/15 rounded-lg">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Selected Client Notes ({clientsToDisplay.length})</h3>
                            <p className="text-xs text-blue-100 mt-0.5">Use the ↓ icon to apply notes to all selected rows.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-blue-100 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="overflow-auto flex-1 p-0">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gradient-to-b from-sky-500 to-sky-600 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60 w-[30%]">Company</th>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60 w-[70%]">Notes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-100">
                            {clientsToDisplay.map(client => (
                                <NotesRow key={client.id} client={client} onSave={onUpdateClient} onBulkApply={handleBulkApply} />
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg shadow-sm">Done</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NotesListModal;
